// Noyau — TABLEAU D'AVANCEMENT (lot P2, plugin « avancement »). Sorte générique de la chimie : une réaction, des
// quantités initiales, et l'élève remplit le tableau (état initial, en cours, final) et l'avancement maximal.
//
//   model.kernel = {
//     plugin: 'avancement', goal: { kind: 'fill', target: 'tableau' },
//     start: 'CH₄ + 2 O₂ → CO₂ + 2 H₂O',          // l'équation, telle qu'affichée
//     reaction: {
//       reactants: [{ f: 'CH₄', nu: 1, n0: '2' }, { f: 'O₂', nu: 2, n0: '3' }],
//       products:  [{ f: 'CO₂', nu: 1 }, { f: 'H₂O', nu: 2 }],     // n0 absent = 0
//       unit: 'mol',                                               // « mol », « mmol »…
//     },                                                           // role: 'excess' | 'solvent' (H⁺, H₂O)
//     aclaims: [ … une affirmation par choix du QCM … ], single?: true, answer: 'A', path: [ligne du tableau]
//   }
//
// La ligne « tableau » est UN texte compact « ⊡{"i":[…],"c":[…],"f":[…],"x":"1,5"} » : les cases de l'état initial,
// de l'état en cours (expressions en x), de l'état final, et x_max ; une case par espèce, réactifs puis produits.
// Le juge vérifie chaque case EXACTEMENT (rationnels) et nomme l'erreur sans donner la réponse ; un tableau juste
// tranche toutes les affirmations (élimination avec `single`).
//
// Affirmations (`aclaims`, une par choix ENREGISTRÉ du QCM) :
//   { kind: 'expr', species, text }            « n(O₂) = 3 − 2x » à l'état en cours
//   { kind: 'at', species, x, value }           quantité de l'espèce quand x vaut x (« 0,4 »)
//   { kind: 'final', species, value }           quantité à l'état final
//   { kind: 'xmax', value }                     avancement maximal
//   { kind: 'limiting', species }               réactif limitant
//   { kind: 'excessLeft', species, value }      réactif en excès et ce qu'il en reste
//   { kind: 'xFrom', species, value, x }        « il reste / il s'est formé value de species » ⇒ x
//   { kind: 'stoich', yes }                     « le mélange est (n'est pas) stœchiométrique »
//   { kind: 'present', species: [f…] }          les espèces présentes à l'état final (exactement celles-là ; le
//                                                solvant et un réactif en excès ne comptent pas)
//   { kind: 'other' }                           fausse (tranchée par élimination)
import { num, Q } from './ast.js';
import { parse } from './syntax.js';
import { evaluate } from './equiv.js';
import { ratText, decText } from './pedagogy.js';

export const ADV_MARK = '⊡';

const toQ = (v) => (v && typeof v === 'object' && typeof v.p === 'number' ? num(v.p, v.q) : null);
const ZERO = num(0);
const norm = (s) =>
  String(s ?? '')
    .normalize('NFC')
    .replace(/\s+/g, ' ')
    .trim();
const qText = (v) => decText(v) ?? ratText(v);

/** Un nombre écrit (« 0,4 », « 3/2 ») → rationnel, ou null. */
function readQ(text, table) {
  try {
    return toQ(evaluate(parse(String(text), table), {}, table));
  } catch {
    return null;
  }
}

/** Le modèle de la feuille : espèces, quantités initiales, avancement maximal, réactif(s) limitant(s). */
export function advModel(model, table) {
  const r = model?.kernel?.reaction;
  if (!r || !Array.isArray(r.reactants) || !Array.isArray(r.products))
    throw new Error('avancement : il faut kernel.reaction { reactants, products }');
  const species = [
    ...r.reactants.map((s) => ({ ...s, side: 'r' })),
    ...r.products.map((s) => ({ ...s, side: 'p' })),
  ].map((s) => {
    const nu = Number(s.nu ?? 1);
    if (!(nu > 0) || !Number.isInteger(nu)) throw new Error(`avancement : coefficient de ${s.f}`);
    const role = s.role === 'excess' || s.role === 'solvent' ? s.role : null;
    const n0 = role ? null : s.n0 == null ? ZERO : readQ(s.n0, table);
    if (!role && !n0) throw new Error(`avancement : quantité initiale de ${s.f}`);
    return { f: norm(s.f), nu, side: s.side, role, n0 };
  });
  const limiters = species.filter((s) => s.side === 'r' && !s.role);
  if (!limiters.length) throw new Error('avancement : aucun réactif ne limite');
  const ratios = limiters.map((s) => ({ s, x: Q.div(s.n0, num(s.nu)) }));
  let xmax = ratios[0].x;
  for (const r2 of ratios) if (r2.x.p * xmax.q < xmax.p * r2.x.q) xmax = r2.x;
  const limiting = ratios.filter((r2) => Q.eq(r2.x, xmax)).map((r2) => r2.s.f);
  const amount = (s, x) =>
    s.side === 'r' ? Q.sub(s.n0, Q.mul(num(s.nu), x)) : Q.add(s.n0, Q.mul(num(s.nu), x));
  return {
    table,
    species,
    unit: r.unit ?? 'mol',
    xmax,
    limiting,
    ratios,
    amount,
    claims: Array.isArray(model.kernel.aclaims) ? model.kernel.aclaims : [],
    single: !!model.kernel.single,
  };
}

/** Écriture compacte d'un tableau rempli. */
export function advText(view) {
  const row = (r) => (r || []).map((c) => String(c ?? ''));
  return `${ADV_MARK}${JSON.stringify({ i: row(view.i), c: row(view.c), f: row(view.f), x: String(view.x ?? '') })}`;
}

/** Relit un tableau « ⊡{…} » ; null si ce n'en est pas un. */
export function parseAdvText(text) {
  const t = String(text ?? '');
  if (!t.startsWith(ADV_MARK)) return null;
  try {
    const b = JSON.parse(t.slice(1));
    const ok = (r) => Array.isArray(r) && r.every((c) => typeof c === 'string' && c.length <= 24);
    if (!b || !ok(b.i) || !ok(b.c) || !ok(b.f) || typeof b.x !== 'string') return null;
    return { i: b.i, c: b.c, f: b.f, x: b.x };
  } catch {
    return null;
  }
}

const WORD = { excess: 'excès', solvent: 'solvant' };

/** Une case d'expression en x : sa valeur en quelques x (pour comparer à n₀ ± ν x). */
function exprAt(text, table, x) {
  try {
    return toQ(evaluate(parse(String(text), table), { x }, table));
  } catch {
    return null;
  }
}
const PROBES = [num(0), num(1), num(2, 7)];

/**
 * Juge un tableau : { cells: { 'i.0' | 'c.1' | 'f.2' | 'x' → { status, title, message } }, done, first }.
 * `first` = la première case fausse (dans l'ordre de lecture), pour le message de la ligne.
 */
export function judgeAdv(M, view) {
  const cells = {};
  const n = M.species.length;
  const bad = (title, message) => ({ status: 'wrong', title, message });
  const ok = { status: 'ok' };
  for (let k = 0; k < n; k++) {
    const s = M.species[k];
    for (const row of ['i', 'c', 'f']) {
      const text = norm(view[row]?.[k]);
      const id = `${row}.${k}`;
      if (!text) {
        cells[id] = { status: 'empty' };
        continue;
      }
      if (s.role) {
        cells[id] =
          text.toLowerCase() === WORD[s.role]
            ? ok
            : bad(
                s.role === 'excess' ? 'En excès' : 'Le solvant',
                s.role === 'excess'
                  ? `${s.f} est en excès : sa quantité ne limite pas la réaction, on écrit « excès ».`
                  : `${s.f} est le solvant : on écrit « solvant ».`,
              );
        continue;
      }
      if (row === 'c') {
        const vals = PROBES.map((x) => exprAt(text, M.table, x));
        const want = PROBES.map((x) => M.amount(s, x));
        if (vals.some((v) => !v)) {
          cells[id] = bad(
            'Une expression en x',
            'Écris la quantité en fonction de x, comme « 3 − 2x ».',
          );
          continue;
        }
        if (vals.every((v, j) => Q.eq(v, want[j]))) {
          cells[id] = ok;
          continue;
        }
        const slope = Q.sub(vals[1], vals[0]);
        const wantSlope = s.side === 'r' ? num(-s.nu) : num(s.nu);
        if (!Q.eq(vals[0], s.n0))
          cells[id] = bad(
            'L’état initial',
            'Pour x = 0, on doit retrouver la quantité de l’état initial.',
          );
        else if (Q.eq(slope, Q.neg(wantSlope)))
          cells[id] = bad(
            s.side === 'r' ? 'Un réactif disparaît' : 'Un produit se forme',
            s.side === 'r'
              ? `${s.f} est un réactif : sa quantité DIMINUE quand x augmente.`
              : `${s.f} est un produit : sa quantité AUGMENTE quand x augmente.`,
          );
        else
          cells[id] = bad(
            'Le coefficient',
            `Quand x augmente de 1 ${M.unit}, la quantité de ${s.f} change de ${s.nu} ${M.unit} : c’est son coefficient dans l’équation.`,
          );
        continue;
      }
      const v = readQ(text, M.table);
      if (!v) {
        cells[id] = bad('Un nombre', 'Cette case attend une quantité (un nombre).');
        continue;
      }
      if (row === 'i') {
        cells[id] = Q.eq(v, s.n0)
          ? ok
          : bad(
              'L’état initial',
              s.side === 'p' && s.n0.p === 0
                ? 'Au départ, il n’y a aucun produit : on écrit 0.'
                : 'Recopie la quantité initiale donnée par l’énoncé.',
            );
        continue;
      }
      const want = M.amount(s, M.xmax);
      if (Q.eq(v, want)) cells[id] = ok;
      else if (v.p < 0)
        cells[id] = bad(
          'Une quantité négative',
          'Une quantité de matière n’est jamais négative : x_max est trop grand.',
        );
      else
        cells[id] = bad(
          'L’état final',
          'À l’état final, remplace x par x_max dans la ligne « en cours ».',
        );
    }
  }
  const xt = norm(view.x);
  if (!xt) cells.x = { status: 'empty' };
  else {
    const v = readQ(xt, M.table);
    if (v && Q.eq(v, M.xmax)) cells.x = { status: 'ok' };
    else if (!v) cells.x = bad('Un nombre', 'x_max est une quantité (un nombre).');
    else if (M.ratios.length > 1 && M.ratios.some((r) => Q.eq(r.x, v)))
      cells.x = {
        status: 'trap',
        title: 'Le plus petit',
        message:
          'Chaque réactif s’épuise pour une valeur de x : le premier épuisé arrête la réaction, x_max est la PLUS PETITE.',
      };
    else if (M.ratios.some((r) => Q.eq(r.s.n0, v)))
      cells.x = {
        status: 'trap',
        title: 'Le coefficient',
        message: 'Un réactif s’épuise quand n₀ − ν x = 0 : x = n₀ ÷ ν, pas n₀.',
      };
    else
      cells.x = bad(
        'L’avancement maximal',
        'Pour chaque réactif, cherche x tel que n₀ − ν x = 0 ; x_max est la plus petite valeur.',
      );
  }
  const order = [...['i', 'c', 'f'].flatMap((row) => M.species.map((_, k) => `${row}.${k}`)), 'x'];
  const first = order
    .map((id) => [id, cells[id]])
    .find(([, c]) => c && c.status !== 'ok' && c.status !== 'empty');
  const done = order.every((id) => cells[id]?.status === 'ok');
  return { cells, done, first: first ? { id: first[0], ...first[1] } : null };
}

// ── affirmations ───────────────────────────────────────────────────────────
function speciesOf(M, f) {
  const k = M.species.findIndex((s) => s.f === norm(f));
  return k >= 0 ? M.species[k] : null;
}
/** Vérité EXACTE d'une affirmation (indépendante de ce qu'écrit l'élève). */
export function advClaimTruth(M, c) {
  const s = c.species ? speciesOf(M, c.species) : null;
  const v = c.value != null ? readQ(c.value, M.table) : null;
  switch (c.kind) {
    case 'expr':
      if (!s || s.role) return false;
      return PROBES.every((x) => {
        const got = exprAt(c.text, M.table, x);
        return !!got && Q.eq(got, M.amount(s, x));
      });
    case 'at': {
      const x = readQ(c.x, M.table);
      return !!(s && !s.role && v && x && Q.eq(v, M.amount(s, x)));
    }
    case 'final':
      return !!(s && !s.role && v && Q.eq(v, M.amount(s, M.xmax)));
    case 'xmax':
      return !!(v && Q.eq(v, M.xmax));
    case 'limiting':
      return !!s && M.limiting.includes(s.f) && M.limiting.length === 1;
    case 'excessLeft':
      return (
        !!(s && !s.role && v) &&
        s.side === 'r' &&
        !M.limiting.includes(s.f) &&
        Q.eq(v, M.amount(s, M.xmax))
      );
    case 'stoich': {
      const all = M.ratios.every((r) => Q.eq(r.x, M.xmax));
      return all === !!c.yes;
    }
    case 'present': {
      const want = new Set((c.species || []).map(norm));
      const have = M.species.filter((x) => !x.role && M.amount(x, M.xmax).p > 0).map((x) => x.f);
      return have.length === want.size && have.every((f) => want.has(f));
    }
    case 'xFrom': {
      const x = c.x != null ? readQ(c.x, M.table) : null;
      return !!(s && !s.role && v && x && Q.eq(M.amount(s, x), v));
    }
    default:
      return false;
  }
}

// ── état de la feuille ─────────────────────────────────────────────────────
export function createAdvState(model, table) {
  const M = advModel(model, table);
  return { M, done: false, decisions: M.claims.map(() => null), table: null };
}
const cloneAdv = (ks) => ({ ...ks, decisions: [...ks.decisions] });

/** L'équation de la réaction : la ligne de départ de la feuille. */
export function advStartText(model) {
  return String(model?.kernel?.start ?? '');
}

/** Les décisions de toutes les affirmations, une fois le tableau juste (élimination avec `single`). */
function decideAll(M) {
  const truths = M.claims.map((c) => advClaimTruth(M, c));
  return truths;
}

/** Verdict d'une ligne : un tableau « ⊡{…} » (pour l'instant la seule ligne de cette feuille). */
export function judgeAdvLine(ks, text) {
  const view = parseAdvText(text);
  if (!view)
    return {
      status: 'parse_error',
      kind: null,
      gestures: [],
      hora: 0,
      message: 'Remplis le tableau d’avancement (bouton sous l’énoncé).',
      error: { pos: 0, len: 1, msg: 'Remplis le tableau d’avancement.' },
    };
  if (ks.done)
    return {
      status: 'useless',
      kind: 'kept',
      gestures: [],
      hora: 0,
      title: 'Déjà fait',
      message: 'Ton tableau est déjà juste.',
    };
  const n = ks.M.species.length;
  if (view.i.length !== n || view.c.length !== n || view.f.length !== n)
    return {
      status: 'parse_error',
      kind: null,
      gestures: [],
      hora: 0,
      message: 'Une case par espèce de l’équation.',
      error: { pos: 0, len: 1, msg: 'Une case par espèce.' },
    };
  const J = judgeAdv(ks.M, view);
  if (!J.done) {
    const empty = Object.values(J.cells).some((c) => c.status === 'empty');
    const f = J.first;
    return {
      status: f?.status === 'trap' ? 'trap' : 'wrong',
      kind: null,
      gestures: [],
      hora: 0,
      title: f?.title ?? 'Le tableau n’est pas fini',
      message: f?.message ?? (empty ? 'Remplis toutes les cases.' : ''),
      cells: J.cells,
      ...(f?.status === 'trap' ? { trap: { title: f.title, why: f.message } } : {}),
    };
  }
  const next = cloneAdv(ks);
  next.done = true;
  next.table = view;
  next.decisions = decideAll(ks.M);
  const okCells = Object.values(J.cells).filter((c) => c.status === 'ok').length;
  const decided = next.decisions.flatMap((t, index) =>
    t !== null && ks.decisions[index] === null ? [{ index, truth: t }] : [],
  );
  return {
    status: 'goal',
    kind: 'step',
    gestures: [
      {
        ruleId: 'avancement.table',
        label: 'Tableau d’avancement',
        why: 'État initial, état en cours (n₀ ± ν x), avancement maximal et état final.',
        hora: okCells,
      },
    ],
    hora: Math.min(12, okCells),
    title: 'Tableau d’avancement juste',
    message: 'Tableau juste : tu peux lire la réponse.',
    cells: J.cells,
    decided,
    adv: next,
  };
}

/** Décisions (true / false / null) des affirmations ; une seule bonne réponse : les autres sont fausses. */
export function advDecisions(ks) {
  if (!ks.done) return ks.decisions;
  if (!ks.M.single) return ks.decisions;
  const n = ks.decisions.filter((d) => d === true).length;
  return n === 1 ? ks.decisions.map((d) => d === true) : ks.decisions;
}

/** Ce que l'éditeur affiche : espèces, unité, rôles. */
export function advLayout(ks) {
  return {
    species: ks.M.species.map((s) => ({ f: s.f, nu: s.nu, side: s.side, role: s.role })),
    unit: ks.M.unit,
  };
}

/** Le tableau juste (Corrigé). */
export function advSolution(ks) {
  const M = ks.M;
  const cell = (s, row) =>
    s.role
      ? WORD[s.role]
      : row === 'i'
        ? qText(s.n0)
        : row === 'c'
          ? s.side === 'r'
            ? `${qText(s.n0)} − ${s.nu === 1 ? '' : s.nu}x`
            : s.n0.p === 0
              ? `${s.nu === 1 ? '' : s.nu}x`
              : `${qText(s.n0)} + ${s.nu === 1 ? '' : s.nu}x`
          : qText(M.amount(s, M.xmax));
  return {
    i: M.species.map((s) => cell(s, 'i')),
    c: M.species.map((s) => cell(s, 'c')),
    f: M.species.map((s) => cell(s, 'f')),
    x: qText(M.xmax),
  };
}
